import {
  Badge,
  Banner,
  Button,
  Code,
  Dialog,
  Empty,
  Input,
  Loader,
  Select,
  Surface,
  Table,
  Tabs,
  Text,
} from '@cloudflare/kumo'
import { ArrowsClockwise, Flask, Repeat, User, X } from '@phosphor-icons/react'
import {
  LANGUAGES,
  PRODUCT_CATEGORIES,
  PRODUCT_DRAFT_COPY,
  SHELF_LIFE_COPY,
  productCategoryLabel,
  taxonomyLabel,
  type AdminSimulationListQuery,
  type SimulationStatus,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { adminApi, type Page, type SimulationDetail, type SimulationListItem } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { GenerationRecovery } from '../components/generation-recovery'
import { HelpPanel } from '../components/help-panel'
import { MenuTagList } from '../components/menu-tag-list'
import { PageHeader } from '../components/page-header'
import {
  adminCostView,
  AI_JUDGE_SAFETY_NOTE,
  AI_JUDGE_SCORE_RULE,
  AUTO_CHECK_RULE,
} from '../lib/admin-cost-hold'
import { useGetToken } from '../lib/auth'
import { menuTagRows } from '../lib/community-concerns'
import { fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePage } from '../lib/use-page'

const STATUS_BADGE: Record<SimulationStatus, 'neutral' | 'info' | 'success' | 'error'> = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'error',
  cancelled: 'neutral',
}
const STATUS_LABEL: Record<SimulationStatus, string> = {
  queued: '대기',
  running: '생성 중',
  done: '완료',
  failed: '실패',
  cancelled: '사용자 취소',
}

const ALL = 'all'
const STATUS_ITEMS = [
  { label: '상태: 전체', value: ALL },
  ...(['done', 'failed', 'running', 'queued', 'cancelled'] as const).map((s) => ({
    label: `상태: ${STATUS_LABEL[s]}`,
    value: s,
  })),
]
const GATE_ITEMS = [
  { label: '게이트: 전체', value: ALL },
  { label: '게이트: 통과', value: 'passed' },
  { label: '게이트: best-of (미통과)', value: 'bestof' },
]
const CATEGORY_ITEMS = [
  { label: '카테고리: 전체', value: ALL },
  ...PRODUCT_CATEGORIES.map((c) => ({ label: c.label, value: c.id })),
]
const LANG_ITEMS = [
  { label: '언어: 전체', value: ALL },
  ...LANGUAGES.map((l) => ({ label: `언어: ${l}`, value: l })),
]
/** 게이트의 코드 검사 12개 — 한 줄 설명은 상세의 Gate 탭에도 쓴다 */
const CHECKS: Record<string, string> = {
  'steps-count': '공정 단계 수가 카테고리 규칙 범위 안인가',
  'cost-math': '재료+포장+인건비 = 총원가가 맞는가',
  'margin-positive': '마진이 15%를 넘는가',
  'ingredient-costs': '재료 원가 = 수량 × 단가인가',
  'retail-in-market-range': '판매가가 골든셋 가격대 안인가',
  'allergens-in-market': '알레르겐이 그 시장의 표시 대상인가',
  'name-not-recent': '제품명이 최근 50개와 겹치지 않는가',
  language: '한 언어로만 쓰였는가',
  'plain-layer': '소비자용 쉬운 공정 설명이 있는가',
  'plain-name': '제품명이 소비자 언어인가',
  'estPh-present': '추정 pH 가 비어 있지 않은가',
  'no-questions-to-user': '사용자에게 되묻는 문장이 없는가',
}
const CHECK_ITEMS = [
  { label: '실패한 검사: 전체', value: ALL },
  ...Object.keys(CHECKS).map((k) => ({ label: `실패: ${k}`, value: k })),
]

type Filters = Pick<
  AdminSimulationListQuery,
  'status' | 'gate' | 'categoryId' | 'language' | 'check' | 'q' | 'userId'
>

/**
 * 시뮬레이션 관측 — 생성마다 돌아간 게이트(코드 검사 + AI 심사) 결과와 소요 시간, 채택된 결과.
 * 필터로 실패·best-of·특정 검사 실패만 골라 보고, 같은 입력으로 재실행해 프롬프트 변경을 비교한다.
 */
export function SimulationsPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { query, setPage } = usePage()
  const [filters, setFilters] = useState<Filters>(() => ({
    userId: params.get('userId') ?? undefined,
  }))
  const [search, setSearch] = useState('')
  const [data, setData] = useState<Page<SimulationListItem> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [open, setOpen] = useState<SimulationDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const items = data?.items ?? null

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setData(await adminApi.simulations.list(getToken, { ...query, ...filters }))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken, query, filters])

  useEffect(() => {
    void reload()
  }, [reload])

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K] | typeof ALL | null) => {
    setPage(1)
    setFilters((f) => ({ ...f, [key]: value === ALL || value === null ? undefined : value }))
  }

  const openDetail = useCallback(
    async (id: string) => {
      try {
        const { simulation } = await adminApi.simulations.get(getToken, id)
        setOpen(simulation)
      } catch (err) {
        setError(toMessage(err))
      }
    },
    [getToken],
  )

  // 견적·사용자 화면에서 ?open=<id> 로 넘어온 경우
  const openParam = params.get('open')
  useEffect(() => {
    if (openParam) void openDetail(openParam)
  }, [openParam, openDetail])

  const closeDetail = () => {
    setOpen(null)
    if (openParam) {
      const next = new URLSearchParams(params)
      next.delete('open')
      setParams(next, { replace: true })
    }
  }

  const activeFilters = Object.values(filters).filter(Boolean).length

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="시뮬레이션"
        description="사용자가 만든 제품 하나하나예요. 생성마다 게이트(코드 검사 12개 + AI 심사)를 거치고, 실패하면 최대 3번 다시 만들어요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      <HelpPanel
        id="simulations"
        steps={[
          <>
            제품명을 누르면 상세가 열려요. <b>요약</b>(사용자가 보는 내용) · <b>게이트</b>(검사
            결과) · <b>결과 JSON</b> · <b>내부 메모</b>(사용자에게 안 보이는 AI 가정·시도 기록) 4개
            탭이에요.
          </>,
          <>
            품질을 볼 때는 필터에서 <b>게이트: best-of</b> 를 고르세요 — 3번 다 검사를 못 넘고 나간
            결과예요. <b>실패한 검사</b> 필터로 어떤 검사에서 자주 떨어지는지 좁힐 수 있어요.
          </>,
          <>
            프롬프트나 골든셋을 바꾼 뒤에는 상세의 <b>같은 입력으로 재실행</b>으로 전후를
            비교하세요. 새 제품이 원 소유자 계정에 하나 더 생겨요.
          </>,
        ]}
        notes={[
          `자동 점검 열의 "2×" 는 2번째 시도에서 채택됐다는 뜻이에요. AI 점검 열의 4/5/4 는 현실성/일관성/안전에 대한 AI 초안 점수(${AI_JUDGE_SCORE_RULE})예요.`,
          '재실행은 AI 비용이 들어요(제품 1개 분량).',
          '메뉴로 만든 제품은 요약 탭에 메뉴 꼬리표가 나와요. 사용자가 고른 것만 있어서 비어 있을 수 있어요.',
        ]}
      />

      {error && <Banner variant="error" title="불러오지 못했어요" description={error} />}
      {notice && <Banner variant="default" title={notice} />}

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Select
            aria-label="상태"
            size="sm"
            className="min-w-36"
            value={filters.status ?? ALL}
            onValueChange={(v: string | null) => setFilter('status', v as Filters['status'])}
            items={STATUS_ITEMS}
          />
          <Select
            aria-label="게이트"
            size="sm"
            className="min-w-48"
            value={filters.gate ?? ALL}
            onValueChange={(v: string | null) => setFilter('gate', v as Filters['gate'])}
            items={GATE_ITEMS}
          />
          <Select
            aria-label="실패한 검사"
            size="sm"
            className="min-w-56"
            value={filters.check ?? ALL}
            onValueChange={(v: string | null) => setFilter('check', v)}
            items={CHECK_ITEMS}
          />
          <Select
            aria-label="카테고리"
            size="sm"
            className="min-w-48"
            value={filters.categoryId ?? ALL}
            onValueChange={(v: string | null) =>
              setFilter('categoryId', v as Filters['categoryId'])
            }
            items={CATEGORY_ITEMS}
          />
          <Select
            aria-label="언어"
            size="sm"
            className="min-w-32"
            value={filters.language ?? ALL}
            onValueChange={(v: string | null) => setFilter('language', v as Filters['language'])}
            items={LANG_ITEMS}
          />
          <div className="w-56">
            <Input
              aria-label="제품명 검색"
              size="sm"
              placeholder="제품명 검색 후 Enter"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === 'Enter' && setFilter('q', search.trim() || null)
              }
            />
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              icon={<X />}
              onClick={() => {
                setSearch('')
                setPage(1)
                setFilters({})
                setParams({}, { replace: true })
              }}
            >
              필터 지우기
            </Button>
          )}
        </div>
        {filters.userId && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">사용자: {filters.userId}</Badge>
            <Button
              variant="ghost"
              size="sm"
              icon={<User />}
              onClick={() => navigate(`/users?id=${encodeURIComponent(filters.userId ?? '')}`)}
            >
              사용자 카드
            </Button>
          </div>
        )}

        <Surface className="overflow-x-auto rounded-lg p-0">
          {items === null ? (
            <div className="flex items-center gap-3 p-6">
              <Loader />
              <Text variant="secondary">불러오는 중…</Text>
            </div>
          ) : items.length === 0 ? (
            <Empty
              icon={<Flask size={40} className="text-kumo-inactive" />}
              title={
                activeFilters > 0 ? '조건에 맞는 시뮬레이션이 없어요' : '아직 시뮬레이션이 없어요'
              }
              description={
                activeFilters > 0
                  ? '필터를 바꾸거나 지워 보세요.'
                  : '앱에서 제품을 만들면 여기에 나타나요.'
              }
            />
          ) : (
            <Table>
              <Table.Header variant="compact">
                <Table.Row>
                  <Table.Head>만든 시각</Table.Head>
                  <Table.Head>제품</Table.Head>
                  <Table.Head>카테고리</Table.Head>
                  <Table.Head>언어·시장</Table.Head>
                  <Table.Head>상태</Table.Head>
                  <Table.Head>자동 점검</Table.Head>
                  <Table.Head>AI 점검</Table.Head>
                  <Table.Head>소요</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.map((s) => {
                  const ev = s.evaluation
                  const failed = ev?.checks.filter((c) => !c.ok).map((c) => c.name) ?? []
                  return (
                    <Table.Row key={s.id} data-testid="simulation-row">
                      <Table.Cell className="whitespace-nowrap">
                        <span className="text-kumo-subtle text-xs">{fmtTime(s.createdAt)}</span>
                      </Table.Cell>
                      <Table.Cell className="max-w-72">
                        <button
                          type="button"
                          className="text-kumo-link block max-w-full truncate text-left font-medium hover:underline"
                          onClick={() => void openDetail(s.id)}
                        >
                          {s.productName ?? '(이름 없음)'}
                        </button>
                        {s.error && (
                          <div className="text-kumo-danger truncate text-xs" title={s.error}>
                            {s.error}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant="outline">{productCategoryLabel(s.categoryId)}</Badge>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <span className="text-xs">
                          {s.language} · {s.market}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge appearance="dot" variant={STATUS_BADGE[s.status]}>
                          {STATUS_LABEL[s.status]}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        {ev ? (
                          <span
                            className="text-xs"
                            title={failed.length ? `실패: ${failed.join(', ')}` : undefined}
                          >
                            <Badge variant={ev.passed ? 'success' : 'warning'}>
                              {ev.passed ? '자동 점검 통과' : 'best-of'}
                            </Badge>{' '}
                            {ev.attempts}×
                          </span>
                        ) : (
                          <span className="text-kumo-subtle">—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {ev?.judge ? (
                          <span className="text-xs tabular-nums" title={ev.judge.feedback}>
                            {ev.judge.realism}/{ev.judge.consistency}/{ev.judge.safety}
                          </span>
                        ) : (
                          <span className="text-kumo-subtle">—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-xs tabular-nums">
                          {ev?.timingsMs['total']
                            ? `${Math.round(ev.timingsMs['total'] / 1000)}초`
                            : '—'}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          )}
        </Surface>

        {data && (
          <TablePagination
            page={query.page}
            setPage={setPage}
            perPage={query.perPage}
            total={data.total}
          />
        )}
      </div>

      <Dialog.Root open={open !== null} onOpenChange={(o) => !o && closeDetail()}>
        <Dialog size="xl" className="max-h-[90vh] overflow-y-auto p-6">
          {open && (
            <DetailBody
              key={open.id}
              sim={open}
              onClose={closeDetail}
              onChanged={(simulation) => {
                setOpen((current) => (current?.id === simulation.id ? simulation : current))
                void reload()
              }}
              onRerun={(newId) => {
                closeDetail()
                setNotice(`재실행을 시작했어요 (${newId}). 1~2분 뒤 목록 맨 위에 나타나요.`)
                void reload()
              }}
            />
          )}
        </Dialog>
      </Dialog.Root>
    </div>
  )
}

function DetailBody({
  sim,
  onClose,
  onRerun,
  onChanged,
}: {
  sim: SimulationDetail
  onClose: () => void
  onRerun: (newId: string) => void
  onChanged: (simulation: SimulationDetail) => void
}) {
  const getToken = useGetToken()
  const [tab, setTab] = useState('summary')
  const [confirmRerun, setConfirmRerun] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ev = sim.evaluation
  const r = sim.result
  const cost = r ? adminCostView(r) : null

  const rerun = async () => {
    setBusy(true)
    try {
      const { id } = await adminApi.simulations.rerun(getToken, sim.id)
      setConfirmRerun(false)
      onRerun(id)
    } catch (err) {
      setError(toMessage(err))
      setConfirmRerun(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Dialog.Title className="text-lg font-semibold">
            {sim.productName ?? '시뮬레이션'}
          </Dialog.Title>
          <Dialog.Description className="text-kumo-subtle">
            {productCategoryLabel(sim.categoryId)} · {sim.language}/{sim.market} ·{' '}
            {STATUS_LABEL[sim.status]} · {fmtTime(sim.createdAt)}
            {(() => {
              const mode = sim.input.mode ?? 'create'
              const axis = sim.input.axis ?? 'product'
              const picked = sim.input.majorId
                ? taxonomyLabel(axis, sim.input.majorId, sim.input.subId ?? null, 'ko')
                : null
              return (
                <>
                  {' '}
                  · {mode === 'analyze' ? '레시피 분석' : '새로 개발'} /{' '}
                  {axis === 'course' ? '코스·테이스팅' : axis === 'menu' ? '메뉴' : '가공식품'}
                  {picked ? ` · ${picked.major}${picked.sub ? ` › ${picked.sub}` : ''}` : ''}
                  {sim.input.images?.length ? ` · 사진 ${sim.input.images.length}장` : ''}
                </>
              )
            })()}
          </Dialog.Description>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            icon={<Repeat />}
            disabled={busy}
            onClick={() => setConfirmRerun(true)}
          >
            같은 입력으로 재실행
          </Button>
          <Button
            variant="secondary"
            shape="square"
            icon={<X />}
            aria-label="닫기"
            onClick={onClose}
          />
        </div>
      </div>

      {error && <Banner variant="error" title="재실행하지 못했어요" description={error} />}

      <GenerationRecovery simulationId={sim.id} onChanged={onChanged} />

      <Tabs
        variant="underline"
        size="sm"
        value={tab}
        onValueChange={setTab}
        tabs={[
          { value: 'summary', label: '요약' },
          { value: 'gate', label: '게이트' },
          { value: 'result', label: '결과 JSON' },
          { value: 'internal', label: '내부 메모' },
        ]}
      />

      <div className="mt-4">
        {tab === 'summary' && (
          <div className="flex flex-col gap-3 text-sm">
            <Text variant="secondary" size="xs">
              사용자가 앱에서 보는 내용이에요.
            </Text>
            <div>
              <Text variant="secondary" size="sm">
                사용자 입력
              </Text>
              <div className="whitespace-pre-wrap">
                {sim.input.messages
                  .map((m) => m.text)
                  .filter(Boolean)
                  .join('\n') || '(카테고리만 선택)'}
              </div>
            </div>
            {sim.input.axis === 'menu' && (
              <div>
                <Text variant="secondary" size="sm">
                  메뉴 꼬리표
                </Text>
                <div className="mt-1">
                  <MenuTagList rows={menuTagRows(sim.input.menuTags, 'menu')} />
                </div>
              </div>
            )}
            {r && (
              <>
                <div>
                  <Text variant="secondary" size="sm">
                    제품
                  </Text>
                  <div>
                    {r.product.name} — {r.product.tagline}
                  </div>
                  <div className="text-kumo-subtle">
                    {r.product.subtype} · {r.product.format} · {r.product.netWeight.amount}{' '}
                    {r.product.netWeight.unit} · {r.product.storage} · {SHELF_LIFE_COPY.ko.label}
                  </div>
                </div>
                <div>
                  <Text variant="secondary" size="sm">
                    소비자용 설명
                  </Text>
                  <div className="text-kumo-subtle">{r.product.howToUse || '—'}</div>
                  <ol className="list-decimal pl-5">
                    {r.plainSteps.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ol>
                  <div>{r.complianceSummary}</div>
                </div>
                <div>
                  <Text variant="secondary" size="sm">
                    원가·판매가 ({r.currency})
                  </Text>
                  <div>
                    {cost?.held === false ? (
                      <>
                        개당 원가 {cost.totalPerUnit} (재료 {r.costs.ingredientsPerUnit} + 포장{' '}
                        {r.costs.packagingPerUnit} + 인건비 {r.costs.laborPerUnit}) · 판매가{' '}
                        {r.pricing.suggestedRetail} · 마진 {cost.marginPct}%
                      </>
                    ) : (
                      <>
                        개당 원가·마진 {cost?.label} · 판매가 {r.pricing.suggestedRetail}
                      </>
                    )}{' '}
                    · 시장 가격대 {r.pricing.marketRange.min}–{r.pricing.marketRange.max}
                  </div>
                </div>
                <div>
                  <Text variant="secondary" size="sm">
                    재료 {r.ingredients.length}개 ({r.batch.units}개 배치 기준)
                  </Text>
                  <ul className="list-disc pl-5">
                    {r.ingredients.map((i) => (
                      <li key={i.id}>
                        {i.name} {i.qty} {i.unit} × {i.unitPrice} = {i.cost}
                        {i.substitutes.length
                          ? ` · 대체재: ${i.substitutes.map((s) => `${s.name} (${s.savings >= 0 ? '−' : '+'}${Math.abs(s.savings)})`).join(', ')}`
                          : ''}
                        {i.risk && i.risk !== '-' ? (
                          <span className="text-kumo-warning"> · 주의: {i.risk}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <Text variant="secondary" size="sm">
                    공정
                  </Text>
                  <ol className="list-decimal pl-5">
                    {r.steps.map((s, i) => (
                      <li key={i}>
                        <b>{s.title}</b> — {s.temp} · {s.time} · {s.equipment} · 손실 {s.lossPct}% ·{' '}
                        {s.minutes}분<div className="text-kumo-subtle">{s.desc}</div>
                        <div className="text-kumo-subtle">관리점: {s.qc}</div>
                      </li>
                    ))}
                  </ol>
                </div>
                <div>
                  <Text variant="secondary" size="sm">
                    규정
                  </Text>
                  <div className="whitespace-pre-wrap">{r.baseNote}</div>
                  <div className="text-kumo-subtle">
                    추정 pH {r.estPh} · 알레르겐 {r.allergens.join(', ') || '—'}
                  </div>
                  <div className="text-kumo-warning">{r.caution}</div>
                </div>
              </>
            )}
            {sim.error && (
              <Banner variant="error" title="파이프라인 오류" description={sim.error} />
            )}
          </div>
        )}
        {tab === 'gate' &&
          (ev ? (
            <div className="flex flex-col gap-3 text-sm">
              <Text variant="secondary" size="xs">
                초안의 자동 점검 기록이에요. {AUTO_CHECK_RULE} 3번 다 실패하면 가장 점수가 높은
                시도를 내보내고 "best-of" 로 표시해요.
              </Text>
              <Text variant="secondary" size="xs">
                {PRODUCT_DRAFT_COPY.ko} 자동 점검은 실제 시식·제조 시험, 안전성 또는 소비기한 검증이
                아닙니다.
              </Text>
              <div>
                <Badge variant={ev.passed ? 'success' : 'warning'}>
                  {ev.passed ? '자동 점검 통과' : 'best-of (3번 다 실패해 최선을 채택)'}
                </Badge>{' '}
                {ev.attempts}번째 시도에서 채택 ·{' '}
                {Object.entries(ev.timingsMs)
                  .map(([k, v]) => `${k} ${Math.round(v / 1000)}초`)
                  .join(' · ')}
              </div>
              <ul className="flex flex-col gap-1">
                {ev.checks.map((c) => (
                  <li key={c.name} className={c.ok ? '' : 'text-kumo-danger'}>
                    {c.ok ? '✓' : '✗'} <span className="font-mono text-xs">{c.name}</span>
                    <span className="text-kumo-subtle"> — {CHECKS[c.name] ?? ''}</span>
                    {c.detail ? (
                      <div className="text-kumo-subtle pl-5 text-xs">{c.detail}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {ev.judge ? (
                <div>
                  <div>
                    AI 초안 점검: 현실성 {ev.judge.realism} · 일관성 {ev.judge.consistency} · 안전{' '}
                    {ev.judge.safety}
                  </div>
                  <div className="text-kumo-subtle">
                    ({AI_JUDGE_SCORE_RULE}) {AI_JUDGE_SAFETY_NOTE}
                  </div>
                  <div className="text-kumo-subtle whitespace-pre-wrap">{ev.judge.feedback}</div>
                </div>
              ) : (
                <div className="text-kumo-subtle">AI 심사를 받지 못했어요.</div>
              )}
            </div>
          ) : (
            <Text variant="secondary">아직 평가가 없어요.</Text>
          ))}
        {tab === 'result' && (
          <div className="flex flex-col gap-2">
            <Text variant="secondary" size="xs">
              앱에 내려가는 결과 데이터 그대로예요.
            </Text>
            <Code
              lang="jsonc"
              code={JSON.stringify(sim.result, null, 2)}
              className="max-h-[60vh]"
            />
          </div>
        )}
        {tab === 'internal' && (
          <div className="flex flex-col gap-2">
            <Text variant="secondary" size="xs">
              사용자에게 보이지 않는 서버 메모예요 — AI 가 임의로 정한 가정(assumptions), 참고한
              골든셋(comparableIds), 시도별 점수와 피드백(attempts), 찾은 재료 단가(prices), 사진
              브리프.
            </Text>
            <Code
              lang="jsonc"
              code={JSON.stringify(sim.internal, null, 2)}
              className="max-h-[60vh]"
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmRerun}
        title="같은 입력으로 다시 생성할까요?"
        description="원 소유자 계정에 새 제품이 하나 더 생기고, 제품 1개 분량의 AI 비용이 들어요. 사용자의 생성 한도에는 영향을 주지 않고, 사용자에게 완성·실패 알림도 가지 않아요."
        confirmLabel="재실행"
        busy={busy}
        onConfirm={() => void rerun()}
        onClose={() => setConfirmRerun(false)}
      />
    </>
  )
}
